import { type GetServerSideProps } from 'next';

import { seoDataService } from '../../../../services/seoDataService';
import {
  bffService,
  cmsService,
  localizedContentService,
} from '../../../../services';

import { withEnhancedPageHandler } from '../../../../middlewares/withEnhancedPageHandler';

import { user } from '../../../../modules/user';

import { modifiers } from '../../../../data/shared';

import { applyCorrelationIdHeader } from '../../../../utils/server/applyCorrelationIdHeader';

import { PriceTemplate } from '../../../../templates';

import { type IPageInfo } from '../../../../types/services/seoDataService';
import { type IPriceResponse } from '../../../../modules/price/requests/@interfaces';
import {
  type IPricePageProps,
  type IPriceLocalizedContent,
  type TCategoryData,
  type TPricePage,
} from '../../../../modules/price/pageProps/@interfaces';

export const Price = (props: IPricePageProps) => <PriceTemplate {...props} />;

const getServerSidePropsHandler: GetServerSideProps = async ({
  params,
  query,
  res,
  req,
}) => {
  const { serviceSlug } = params;

  const categorySlug = `${params.category}/${params.service}`;

  const refresh = query.refresh === 'true' || false;

  res.setHeader(
    'Cache-Control',
    'public, s-maxage=10, stale-while-revalidate=59'
  );

  const customHeaders = applyCorrelationIdHeader(req.headers);

  const cmsPricePageContent = await cmsService<TPricePage>(
    'api/price-pages',
    {
      'filters[slug][$eq]': serviceSlug,
      'populate[content][populate]': '*',
    },
    customHeaders
  );

  const cmsCategorySlugContent = await cmsService<TPricePage>(
    'api/price-pages',
    {
      'filters[categorySlug][$eq]': categorySlug,
    },
    customHeaders
  );

  if (cmsPricePageContent.length === 0 || cmsCategorySlugContent.length === 0) {
    return {
      notFound: true,
    };
  }

  const pricePage = { ...cmsPricePageContent[0].attributes };

  const pricesByCategory = cmsCategorySlugContent.map((c) => c.attributes);

  const { data: category } = await bffService<unknown, IPriceResponse>({
    path: 'categories/by_slug',
    params: {
      slug: categorySlug,
    },
    method: 'GET',
    customHeaders: {
      ...customHeaders,
      'x-frl-key': `shared-category:${categorySlug}`,
    },
    refresh,
  });

  const dynamicDataCategory: TCategoryData = {
    breadcrumbs: category.breadcrumbs,
    bestReviews: category.best_reviews.slice(0, 3),
    root: category.root,
    semOccupationSingular: category.sem_occupation_singular,
    semOccupationPlural: category.sem_occupation_plural,
  };

  let internalLinks = await seoDataService<Partial<IPageInfo>>({
    slugs: categorySlug,
  });

  if (internalLinks === null) {
    internalLinks = {
      children_flat: [],
      cities: [],
      states: [],
    };
  }

  const { userData: userResponse } = user.getData(req);
  const { notificationCount } = await user.getNotifications(req);

  const userData = userResponse
    ? {
        ...userResponse,
        isAuthenticated: true,
        notificationCount,
      }
    : {
        avatar: null,
        name: null,
        phone: null,
        isAuthenticated: false,
        notificationCount,
      };

  const { footerContent, headerContent } =
    localizedContentService<IPriceLocalizedContent>(
      'pt-BR',
      ['footerContent', 'headerContent'],
      [modifiers.STATIC_ASSETS_PATH, modifiers.CURRENT_YEAR]
    );

  return {
    props: {
      dynamicDataCategory,
      internalLinks,
      serviceSlug,
      footerContent,
      headerContent,
      userData,
      dynamicDataCms: {
        pricePage,
        pricesByCategory,
      },
    },
  };
};

export const getServerSideProps = withEnhancedPageHandler(
  getServerSidePropsHandler
);

export default Price;
